/**
 * WebMCP tool registration — the browser API that exposes our tools to an agent
 * running in the page (Chrome origin trial, Chrome 149+).
 *
 * The API changed under us mid-trial, and every change hit what we used:
 *  - the entry point moved `Navigator` → `Document` (spec 2026-05-27);
 *    `navigator.modelContext` is DEPRECATED in Chrome 150 but still an alias
 *  - `provideContext()` and `clearContext()` were REMOVED from the spec
 *    (2026-03-05) — bulk registration is gone
 *  - `unregisterTool()` was REMOVED (2026-04-23) in favour of an `AbortSignal`
 *    passed at registration
 *  - `registerTool()` became PROMISE-returning (spec 2026-06-08): a refused
 *    tool REJECTS, it does not throw. Everything the spec refuses on is
 *    plausible for us — a duplicate name, an empty description, a host page
 *    that disabled origin-keyed agent clusters via `document.domain`
 *    (SecurityError), a frame without the `tools` permissions policy
 *    (NotAllowedError). So every call is awaited: an ignored rejection
 *    surfaces as an unhandled rejection in the HOST page, which in the chrome
 *    extension means someone else's error reporting picks up our failure.
 *
 * Because the extension ships to a RANGE of Chrome versions at once, both
 * generations have to work: prefer `registerTool` + signal, fall back to
 * `provideContext` on builds that predate it. Kept separate from the service so
 * the version handling is testable without a browser.
 *
 * NOTE: `inputSchema` must stay a JavaScript OBJECT here. Chrome 154 made
 * `RegisteredTool#inputSchema` return an object rather than a stringified
 * schema, which only affects reading tools back via `getTools()` — we never do —
 * but pre-stringifying on the way IN would break registration on every version.
 */

/** A tool as handed to the browser. */
export interface WebMcpTool {
    name: string;
    description: string;
    /** JSON Schema, as an OBJECT (never stringified) */
    inputSchema: unknown;
    execute: (args: any) => Promise<unknown> | unknown;
    annotations?: unknown;
}

/** The subset of `modelContext` we touch, across both API generations. */
interface ModelContextLike {
    /** current: one call per tool, unregistered by aborting the signal.
     *  Promise-returning since 2026-06-08; older builds returned void. */
    registerTool?: (tool: WebMcpTool, options?: RegisterToolOptions) => Promise<void> | void;
    /** removed 2026-03-05; still present on older origin-trial builds */
    provideContext?: (context: { tools: WebMcpTool[] }) => void;
}

interface RegisterToolOptions {
    signal?: AbortSignal;
    /** origins allowed to see and run the tool while they embed us */
    exposedTo?: string[];
}

interface ModelContextHost {
    document?: { modelContext?: ModelContextLike };
    navigator?: { modelContext?: ModelContextLike };
}

/** What one window's model context did with our tools. */
export interface WebMcpFrameResult {
    api: "registerTool" | "provideContext";
    registered: number;
    failed: Array<{ name: string; reason: unknown }>;
}

export interface WebMcpRegistrationReport {
    /** one entry per window that exposed WebMCP, ours first */
    frames: WebMcpFrameResult[];
    /** origins passed as `exposedTo` (empty when we are top-level) */
    exposedTo: string[];
    /** the walk stopped at an ancestor on another origin — expected when embedded */
    crossOriginAncestor: boolean;
}

/**
 * The `modelContext` for a window: `document.modelContext` (current), else
 * `navigator.modelContext` (deprecated in Chrome 150, still aliased). Undefined
 * where WebMCP is not available at all.
 *
 * THROWS for a cross-origin window: `document` is not on the HTML spec's
 * cross-origin property whitelist, so reading it is a SecurityError. Callers
 * walking a frame chain must handle that — see {@link registerWebMcpTools}.
 */
export function resolveModelContext(win: Window): ModelContextLike | undefined {
    const host = win as unknown as ModelContextHost;
    return host.document?.modelContext ?? host.navigator?.modelContext;
}

/**
 * Origins to pass as `exposedTo` — our cross-origin ancestors, the spec's way
 * to reach an embedder whose document we cannot touch.
 *
 * Empty at top level, and that matters: a MISSING `exposedTo` is what exposes
 * our tools to the browser's built-in agent, so we never pass an empty array.
 * Untrustworthy or unparseable entries are dropped because a single bad origin
 * rejects the registration (SecurityError) and costs us the whole frame.
 */
export function exposedToOrigins(win: Window): string[] {
    const ancestors = win.location?.ancestorOrigins;
    if (!ancestors) return [];
    const own = win.location.origin;
    const origins: string[] = [];
    for (const origin of Array.from(ancestors as ArrayLike<string>)) {
        if (origin === own || origins.includes(origin)) continue;
        if (isPotentiallyTrustworthy(origin)) origins.push(origin);
    }
    return origins;
}

/** "null" (opaque/sandboxed) and plain http fail this, as the spec requires. */
function isPotentiallyTrustworthy(origin: string): boolean {
    let url: URL;
    try { url = new URL(origin); }
    catch { return false; }
    if (url.protocol === "https:" || url.protocol === "wss:") return true;
    if (url.protocol !== "http:") return false;
    const host = url.hostname;
    return host === "localhost" || host.endsWith(".localhost") || host === "127.0.0.1" || host === "[::1]";
}

/**
 * Register `tools` with our window's model context and with every ancestor we
 * can still reach (same-origin), so an agent looking at the top document finds
 * them too. Cross-origin ancestors are handed `exposedTo` instead — their
 * documents are not ours to write into.
 *
 * @param signal abort it to unregister everything — this REPLACES the removed
 *        `unregisterTool()` / `clearContext()`, and is why the service keeps an
 *        AbortController for its lifetime.
 * @returns what each frame accepted; never throws, never rejects.
 */
export async function registerWebMcpTools(win: Window, tools: WebMcpTool[], signal: AbortSignal): Promise<WebMcpRegistrationReport> {
    const report: WebMcpRegistrationReport = { frames: [], exposedTo: exposedToOrigins(win), crossOriginAncestor: false };

    let current: Window | null = win;
    while (current && !signal.aborted) {
        let ctx: ModelContextLike | undefined;
        try {
            ctx = resolveModelContext(current);
        }
        catch {
            // An ancestor on another origin: reading its document is a
            // SecurityError. Expected when the editor is embedded — exposedTo
            // is what reaches that page, if it granted us the "tools" policy.
            report.crossOriginAncestor = true;
            break;
        }

        if (ctx) {
            const result = await registerInContext(ctx, tools, signal, report.exposedTo);
            if (result) report.frames.push(result);
        }

        // `parent` stays readable cross-origin; it is the document behind it that does not.
        const parent: Window | null = current.parent;
        if (!parent || parent === current) break;
        current = parent;
    }

    return report;
}

async function registerInContext(ctx: ModelContextLike, tools: WebMcpTool[], signal: AbortSignal, exposedTo: string[]): Promise<WebMcpFrameResult | undefined> {
    if (typeof ctx.registerTool === "function") {
        const result: WebMcpFrameResult = { api: "registerTool", registered: 0, failed: [] };
        const options: RegisterToolOptions = exposedTo.length ? { signal, exposedTo } : { signal };
        for (const tool of tools) {
            // A superseded run (its signal aborted while it was awaiting) would
            // otherwise keep re-adding tools the browser has already dropped.
            if (signal.aborted) break;
            try {
                // Awaited on purpose: registration REJECTS on refusal (dupe name,
                // permissions policy, document.domain), and one rejected tool must
                // cost us neither the rest of the list nor a stray unhandled
                // rejection in the host page.
                await ctx.registerTool(tool, options);
                result.registered++;
            }
            catch (reason) {
                result.failed.push({ name: tool.name, reason });
            }
        }
        return result;
    }

    if (typeof ctx.provideContext === "function") {
        const result: WebMcpFrameResult = { api: "provideContext", registered: tools.length, failed: [] };
        try {
            ctx.provideContext({ tools });
        }
        catch (reason) {
            result.registered = 0;
            result.failed = tools.map(t => ({ name: t.name, reason }));
        }
        return result;
    }

    return undefined;
}
